import { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "app/hooks";

import {
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Collapse,
    List,
    ListItem,
    Box,
    Stack,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import BadgeIcon from "@mui/icons-material/Badge";

import {
    useGetAllObjectDetectionModelsQuery,
    useGetReIdentificationPersistedRecordingTaskStatisticsQuery,
    useGetReIdentificationSelectedObjectDetectionModelNamesDossierCapabilityConfigurationSettingQuery,
} from "services/streamingApi/caseManager.api";

import { FeatureFlagGuard } from "features/appFeatures/FeatureFlagGuard";
import { AppFeature } from "features/appFeatures/types/AppFeature";
import { EntityId } from "@reduxjs/toolkit";
import { StatusTotalsBarChart } from "features/cases/common/StatusTotalsBarChart";
import { ReIdentificationStatus } from "types/ReIdentificationStatus";

import { SelectObjectDetectionModelsDialog } from "./SelectObjectDetectionModelsDialog";
import { reIdActions } from "./store/slice";
import { reIdSelectors } from "./store/selectors";
import { ObjectDetectionModelName } from "types/ObjectDetectionModelName";

const reIdentificationStatusSeries = [
    {
        status: ReIdentificationStatus.ResultsReady,
        label: "Results ready",
        color: "#10B981",
    },
    {
        status: ReIdentificationStatus.Scheduled,
        label: "Scheduled",
        color: "#3B82F6",
    },
    {
        status: ReIdentificationStatus.Downloading,
        label: "Downloading",
        color: "#8B5CF6",
    },
    {
        status: ReIdentificationStatus.Analyzing,
        label: "Analyzing",
        color: "#06B6D4",
    },
    {
        status: ReIdentificationStatus.Queued,
        label: "Queued",
        color: "#6B7280",
    },
    {
        status: ReIdentificationStatus.QueuedForRetryByUser,
        label: "Queued for retry by user",
        color: "#F59E0B",
    },
    {
        status: ReIdentificationStatus.Failed,
        label: "Failed",
        color: "#EF4444",
    },
];

export interface ReIdSidebarControlsProps {
    dossierId: EntityId;
}

export function ReIdSidebarControls(props: ReIdSidebarControlsProps) {
    const dispatch = useAppDispatch();
    const [open, setOpen] = useState(false);
    const [isModelsDialogOpen, setIsModelsDialogOpen] = useState(false);

    const selectedModelName = useAppSelector(
        reIdSelectors.selectSelectedObjectDetectionModelName,
    );

    const handleClick = () => {
        setOpen(!open);
    };

    const {
        currentData:
            reIdentificationSelectedObjectDetectionModelNamesDossierCapabilityConfigurationSetting,
        isLoading:
            isLoadingReIdentificationSelectedObjectDetectionModelNamesDossierCapabilityConfigurationSetting,
        isSuccess:
            hasLoadedReIdentificationSelectedObjectDetectionModelNamesDossierCapabilityConfigurationSetting,
    } = useGetReIdentificationSelectedObjectDetectionModelNamesDossierCapabilityConfigurationSettingQuery(
        {
            dossierId: props.dossierId,
        },
    );

    const objectDetectionModelNames =
        reIdentificationSelectedObjectDetectionModelNamesDossierCapabilityConfigurationSetting?.selectedObjectDetectionModelNames ??
        [];

    const {
        data: allObjectDetectionModels,
        isSuccess: isSuccessAllModels,
        isLoading: isLoadingAllModels,
    } = useGetAllObjectDetectionModelsQuery(undefined);

    useEffect(() => {
        if (allObjectDetectionModels) {
            dispatch(
                reIdActions.setObjectDetectionModels(allObjectDetectionModels),
            );
        }
    }, [allObjectDetectionModels, dispatch]);

    useEffect(() => {
        dispatch(
            reIdActions.setReIdentificationSelectedObjectDetectionModelNamesDossierCapabilityConfigurationSetting(
                reIdentificationSelectedObjectDetectionModelNamesDossierCapabilityConfigurationSetting,
            ),
        );
    }, [
        reIdentificationSelectedObjectDetectionModelNamesDossierCapabilityConfigurationSetting,
        dispatch,
    ]);

    const usedModelNames = useMemo(() => {
        if (
            allObjectDetectionModels &&
            reIdentificationSelectedObjectDetectionModelNamesDossierCapabilityConfigurationSetting
        ) {
            const selection = allObjectDetectionModels
                .filter((x) =>
                    reIdentificationSelectedObjectDetectionModelNamesDossierCapabilityConfigurationSetting.selectedObjectDetectionModelNames.some(
                        (name) => name === x.name,
                    ),
                )
                .map((x) => x.displayName);

            return selection.length > 0 ? selection.join(", ") : "None";
        }

        return "Loading...";
    }, [
        reIdentificationSelectedObjectDetectionModelNamesDossierCapabilityConfigurationSetting,
        allObjectDetectionModels,
    ]);

    const {
        data,
        error,
        isError,
        isLoading: isLoadingStatistics,
    } = useGetReIdentificationPersistedRecordingTaskStatisticsQuery(
        {
            dossierId: props.dossierId,
            objectDetectionModelName: selectedModelName,
        },
        {
            pollingInterval: 5000,
        },
    );

    const isLoading =
        isLoadingReIdentificationSelectedObjectDetectionModelNamesDossierCapabilityConfigurationSetting ||
        isLoadingStatistics ||
        isLoadingAllModels;

    return (
        <>
            <ListItemButton onClick={handleClick}>
                <ListItemIcon>
                    <BadgeIcon />
                </ListItemIcon>
                <ListItemText primary="Re-ID details" />
                {open ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse
                in={open}
                timeout="auto"
                unmountOnExit
            >
                <List
                    component="div"
                    disablePadding
                >
                    {isLoading && (
                        <ListItem>
                            <ListItemText primary="Loading..." />
                        </ListItem>
                    )}

                    {!isLoading && (
                        <>
                            <ListItem>
                                <Stack alignItems="flex-start">
                                    <ListItemText
                                        primary={"Models"}
                                        secondary={usedModelNames}
                                    />
                                    <FeatureFlagGuard
                                        accessibleWithFeatureFlags={[
                                            AppFeature.AdminDebugTools,
                                        ]}
                                    >
                                        {isSuccessAllModels && (
                                            <Button
                                                size="small"
                                                startIcon={<EditIcon />}
                                                onClick={() =>
                                                    setIsModelsDialogOpen(true)
                                                }
                                            >
                                                Edit models
                                            </Button>
                                        )}
                                    </FeatureFlagGuard>
                                </Stack>
                            </ListItem>
                            <ListItem>
                                <FormControl
                                    fullWidth
                                    size="small"
                                    disabled={
                                        objectDetectionModelNames.length === 0
                                    }
                                >
                                    <InputLabel id="re-id-object-detection-model-label">
                                        Object detection model
                                    </InputLabel>
                                    <Select<ObjectDetectionModelName>
                                        labelId="re-id-object-detection-model-label"
                                        label="Object detection model"
                                        value={selectedModelName ?? ""}
                                        onChange={(event) =>
                                            dispatch(
                                                reIdActions.setSelectedObjectDetectionModelName(
                                                    event.target
                                                        .value as ObjectDetectionModelName,
                                                ),
                                            )
                                        }
                                    >
                                        {objectDetectionModelNames.map(
                                            (modelName) => (
                                                <MenuItem
                                                    key={modelName}
                                                    value={modelName}
                                                >
                                                    {modelName}
                                                </MenuItem>
                                            ),
                                        )}
                                    </Select>
                                </FormControl>
                            </ListItem>
                            <ListItem>
                                <Stack>
                                    <ListItemText primary={"Progress"} />
                                    {isError && `Error occurred: ${error}`}
                                    {data && (
                                        <Box height={40}>
                                            <StatusTotalsBarChart
                                                dossierDownloadStatusStatistics={
                                                    data
                                                }
                                                series={
                                                    reIdentificationStatusSeries
                                                }
                                                completedStatus={
                                                    ReIdentificationStatus.ResultsReady
                                                }
                                                title="Re-Identification tasks"
                                            />
                                        </Box>
                                    )}
                                </Stack>
                            </ListItem>
                        </>
                    )}
                </List>
            </Collapse>
            <FeatureFlagGuard
                accessibleWithFeatureFlags={[AppFeature.AdminDebugTools]}
            >
                {isModelsDialogOpen &&
                    hasLoadedReIdentificationSelectedObjectDetectionModelNamesDossierCapabilityConfigurationSetting &&
                    reIdentificationSelectedObjectDetectionModelNamesDossierCapabilityConfigurationSetting &&
                    isSuccessAllModels && (
                        <SelectObjectDetectionModelsDialog
                            configurationSetting={
                                reIdentificationSelectedObjectDetectionModelNamesDossierCapabilityConfigurationSetting
                            }
                            closeDialog={() => setIsModelsDialogOpen(false)}
                            objectDetectionModels={allObjectDetectionModels}
                            dossierId={props.dossierId}
                        />
                    )}
            </FeatureFlagGuard>
        </>
    );
}
